import { ReactElement, useMemo } from 'react';
import { useTheme } from '@mui/material';

import { DashboardZoneSaturationZone } from '~services/dashboard/types';
import { formatNumber } from '~utils/formatter';
import { TableColumn } from '~components/Table/types';

import { useDashboardVariantV2CarrierContext } from '../../../../Context';
import {
  DashboardVariantV2CarrierZoneSaturationCollapseTableContentEmptyText,
  DashboardVariantV2CarrierZoneSaturationCollapseTableContentTable,
} from './styles';
import {
  DashboardVariantV2CarrierZoneSaturationTableZoneInfoCell,
  DashboardVariantV2CarrierZoneSaturationTableOccupancyInfoCell,
  DashboardVariantV2CarrierZoneSaturationTableSuggestedActionInfoCell,
} from './CustomCells';

const DashboardVariantV2CarrierZoneSaturationCollapseTableContent =
  (): ReactElement => {
    const currentTheme = useTheme();

    const {
      data: {
        zoneSaturation: {
          data: { zones },
        },
      },
    } = useDashboardVariantV2CarrierContext();

    const isEmpty = useMemo(() => zones.length === 0, [zones.length]);

    const columns = useMemo<TableColumn<DashboardZoneSaturationZone>[]>(
      () => [
        {
          dataField: 'zoneName',
          text: 'Zona',
          formatters: {
            cell: (_cell, row) => (
              <DashboardVariantV2CarrierZoneSaturationTableZoneInfoCell
                driversCount={row.driversCount}
                zoneName={row.zoneName}
              />
            ),
          },
        },
        {
          dataField: 'onTheWayItems',
          text: 'En camino',
          align: {
            header: 'center',
            cell: 'center',
          },
          style: {
            header: { width: 120 },
            cell: {
              color: currentTheme.palette.subscribe.main,
              fontWeight: 'bold',
            },
          },
          formatters: {
            cell: (_cell, row) => formatNumber(row.onTheWayItems),
          },
        },
        {
          dataField: 'inWarehouseItems',
          text: 'Depósito',
          align: {
            header: 'center',
            cell: 'center',
          },
          style: {
            header: { width: 120 },
            cell: {
              color: currentTheme.palette.quaternary.main,
              fontWeight: 'bold',
            },
          },
          formatters: {
            cell: (_cell, row) => formatNumber(row.inWarehouseItems),
          },
        },
        {
          dataField: 'inTransitItems',
          text: 'Tránsito',
          align: {
            header: 'center',
            cell: 'center',
          },
          style: {
            header: { width: 120 },
            cell: {
              color: currentTheme.palette.primary.main,
              fontWeight: 'bold',
            },
          },
          formatters: {
            cell: (_cell, row) => formatNumber(row.inTransitItems),
          },
        },
        {
          dataField: 'capacity',
          text: 'Capacidad',
          align: {
            header: 'center',
            cell: 'center',
          },
          style: {
            header: { width: 130 },
            cell: { fontWeight: 'bold' },
          },
          formatters: {
            cell: (_cell, row) => formatNumber(row.capacity),
          },
        },
        {
          dataField: 'occupancyPercentage',
          text: 'Ocupación',
          style: {
            header: { width: 450 },
          },
          formatters: {
            cell: (_cell, row) => (
              <DashboardVariantV2CarrierZoneSaturationTableOccupancyInfoCell
                levelTypeId={row.saturationLevelTypeId}
                capacity={row.capacity}
                occupancyPercentage={row.occupancyPercentage}
              />
            ),
          },
        },
        {
          dataField: 'availableCapacity',
          text: 'Acción sugerida',
          style: {
            header: { width: 234 },
          },
          formatters: {
            cell: (_cell, row) => (
              <DashboardVariantV2CarrierZoneSaturationTableSuggestedActionInfoCell
                levelTypeId={row.saturationLevelTypeId}
                availableCapacity={row.availableCapacity}
                overCapacity={row.overCapacity}
              />
            ),
          },
        },
      ],
      [currentTheme],
    );

    if (isEmpty) {
      return (
        <DashboardVariantV2CarrierZoneSaturationCollapseTableContentEmptyText>
          Sin actividad
        </DashboardVariantV2CarrierZoneSaturationCollapseTableContentEmptyText>
      );
    }

    return (
      <DashboardVariantV2CarrierZoneSaturationCollapseTableContentTable
        data={zones}
        columns={columns}
        stickyHeader={{ enabled: true, maxHeight: 450 }}
        extractorKeyValue="zoneId"
      />
    );
  };

export default DashboardVariantV2CarrierZoneSaturationCollapseTableContent;
